import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "e4e-appearance-mode";
const DEFAULT_MODE = "day";

const AppearanceContext = createContext(null);

function getSystemMode() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return DEFAULT_MODE;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "night" : "day";
}

function resolveMode(mode) {
  return mode === "system" ? getSystemMode() : mode;
}

export function AppearanceProvider({ children }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_MODE;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored || DEFAULT_MODE;
  });
  const [resolvedMode, setResolvedMode] = useState(() => resolveMode(mode));

  useEffect(() => {
    const nextResolved = resolveMode(mode);
    setResolvedMode(nextResolved);

    if (typeof document !== "undefined") {
      document.documentElement.dataset.appearance = nextResolved;
      document.documentElement.dataset.appearanceMode = mode;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
  }, [mode]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (mode === "system") {
        setResolvedMode(resolveMode("system"));
        document.documentElement.dataset.appearance = resolveMode("system");
      }
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    resolvedMode,
    setMode,
    options: [
      { value: "day", label: "Tag" },
      { value: "night", label: "Nacht" },
      { value: "system", label: "System" },
    ],
  }), [mode, resolvedMode]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance() {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error("useAppearance must be used inside AppearanceProvider");
  }
  return context;
}
